<template>
  <div class="insights-unusual-activity-widget-table">
    <AppTable
      :key="isUserDashboard"
      ref="table"
      hoverable
      :loading="loading"
      :selectable="false"
      :table-data="tableData"
      :rows-count="rowsCount"
      alt-field="deactivated"
      td-classes="cursor-pointer"
      @sort="sort"
      @click-row="handleClick"
    >
      <template v-if="isUserDashboard">
        <AppTableColumn
          v-slot="{ row }"
          header="Date"
          name="date"
          :sort-by="buildSortOptions('date')"
        >
          {{ formatDate(row.date) }}
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header="Instances"
          name="instances"
          :sort-by="buildSortOptions('instances')"
        >
          {{ row.instances }}
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header="Total time"
          name="duration_in_minutes"
          :sort-by="buildSortOptions('duration_in_minutes')"
        >
          {{ formatDuration(parseInt(row.duration_in_minutes)) }}
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header="Highest classification"
          :header-tooltip="INSIGHTS.tooltips.confidenceLevel.content"
          header-tooltip-type="info-filled"
          header-classes="no-wrap"
          name="confidence_level"
          :sort-by="buildSortOptions('confidence_level')"
        >
          <InsightsUnusualActivityConfidenceBadge :confidence-level="row.confidence_level" />
        </AppTableColumn>
        <AppTableColumn cell-classes="w-min">
          <AppButton>View</AppButton>
        </AppTableColumn>
      </template>
      <template v-else>
        <AppTableColumn
          v-slot="{ row }"
          header="Member"
          name="name"
          cell-classes="max-width"
          :sort-by="buildSortOptions('name')"
        >
          <InsightsUnusualActivityMemberCell
            :avatar="row.avatar"
            :name="row.name"
            :time-zone="row.time_zone"
            :show-time-zone="isMembersTimeZoneMode"
          />
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header="Total time"
          name="total_duration"
          :sort-by="buildSortOptions('total_duration')"
        >
          {{ formatDuration(row.total_duration) }}
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header="Previous 60 days"
          name="previous_60_days"
          :sort-by="buildSortOptions('previous_60_days')"
        >
          {{ formatDuration(row.previous_60_days) }}
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header="Highest classification"
          :header-tooltip="INSIGHTS.tooltips.confidenceLevel.content"
          header-tooltip-type="info-filled"
          name="confidence_level"
          :sort-by="buildSortOptions('confidence_level')"
        >
          <InsightsUnusualActivityConfidenceBadge :confidence-level="row.confidence_level" />
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          cell-classes="w-min"
        >
          <div class="review-button-wrapper">
            <AppButton>Review</AppButton>
            <div
              v-if="hasPendingNotification(row)"
              class="notification-dot"
            ></div>
          </div>
        </AppTableColumn>
      </template>
    </AppTable>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { INSIGHTS } from '~/app_constants';
import { kebabCase } from 'lodash-es';
import moment from 'moment-timezone';
import { formatDate, secondsToHours } from '~/utils/time_utils';
import InsightsUnusualActivityConfidenceBadge from '~/components/insights/InsightsUnusualActivityConfidenceBadge.vue';
import InsightsUnusualActivityMemberCell from '~/components/insights/InsightsUnusualActivityMemberCell.vue';

export default {
  name: 'InsightsUnusualActivityWidgetTable',
  components: {
    InsightsUnusualActivityConfidenceBadge,
    InsightsUnusualActivityMemberCell,
  },
  props: {
    loading: {
      type: Boolean,
      required: false,
      default: true,
    },
    sortOptions: {
      type: Object,
      required: true,
    },
    timeZone: {
      type: String,
      required: true,
    },
    openDialog: {
      type: Function,
      required: true,
    },
    widgetData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  emits: ['sort', 'switchMember'],
  data: () => ({
    tooltipOptions: Object.freeze({ placement: 'top' }),
    memberRemovedTooltip: 'This member has been removed from the organization',
  }),
  computed: {
    INSIGHTS() {
      return INSIGHTS;
    },
    ...mapGetters(['isUserDashboard', 'filters', 'organizationId', 'isMembersTimeZoneMode']),
    isParentWidgetComponent() {
      return (
        this.$parent?.$parent?.widgetKey === 'unusual_activities_list' ||
        this.$parent?.$parent?.widgetKey === 'unusual_user_activities'
      );
    },
    tableData() {
      return this.isUserDashboard ? this.userTableData : this.teamTableData;
    },
    rowsCount() {
      const defaultCount = this.isParentWidgetComponent ? 'unusualActivityLimit' : 'unusualActivityViewMoreLimit';

      return this.tableData?.length || INSIGHTS.parameters[defaultCount];
    },
    // Array of {date:, instances:, duration_in_minutes:, confidence_level:}
    userTableData() {
      const data = this.widgetData?.unusual_activities || {};

      return Object.values(data);
    },
    teamTableData() {
      return this.widgetData?.users?.map((member) => {
        const memberTotalInstances = member.stats.instances;

        return {
          ...member,
          unusual_activities: {
            stats: memberTotalInstances,
            list: Object.entries(member.stats).map(([key, value]) => ({
              key: kebabCase(key),
              value: this.calculatePercentage(value, memberTotalInstances),
              tooltip: INSIGHTS.unusualActivity.labels[kebabCase(key)]?.text,
            })),
          },
        };
      });
    },
  },
  methods: {
    buildSortOptions(field) {
      return {
        field,
        direction: this.sortOptions?.field === field ? this.sortOptions.direction : null,
      };
    },
    calculatePercentage(value, total) {
      if (!total) {
        return 0;
      }

      return Math.round(((value * 100) / total) * 10) / 10;
    },
    formatDate(date) {
      return formatDate(moment(date));
    },
    formatDuration(minutes) {
      return minutes ? secondsToHours((minutes || 0) * 60) : '—';
    },
    handleClick(row) {
      if (this.isUserDashboard) {
        window.analytics?.track('Unusual instance selected', {
          statsigCustomIDs: ['organizationID', this.organizationId],
        });
        this.openDialog(row.date);
      } else {
        window.analytics?.track('Unusual member selected', {
          statsigCustomIDs: ['organizationID', this.organizationId],
        });
        this.$emit('switchMember', row.id, row.time_zone?.for);
      }
    },
    kebabCase(key) {
      return kebabCase(key);
    },
    sort(options) {
      this.$emit('sort', options);
    },
    hasPendingNotification(row) {
      return Boolean(row?.pending_notification);
    },
  },
};
</script>
